import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Tooltip, Row, Col, Card, message, Image } from 'antd';
import {
    EditOutlined,
    ForkOutlined,
    HeartFilled,
    HeartOutlined,
    DownloadOutlined,
    DeleteOutlined,
    InfoCircleOutlined,
    RedoOutlined,
    UnorderedListOutlined,
    LinkOutlined,
    ArrowRightOutlined,
    ArrowLeftOutlined,
    LoadingOutlined,
    CloseOutlined,
    FullscreenOutlined,
    CheckCircleOutlined,
} from '@ant-design/icons';
import ImageZoom from './ImageZoom';
import HighResModal from './HighResModal';
import CompareSiloView from './CompareSiloView';
import TextEditor from './TextEditor';
import ShareModal from './ShareModal';
import './AIEditorModalViewer.scss';
import './ImageZoom.scss';
import './HighResModal.scss';
import './ShareModal.scss';
import ENVIRONMENT from '../../../../environments';
import axios from 'axios';
import { downloadFile, copyToClipboard } from '../../Utilities';
import { addDownloadIconObserver } from '../../../../utils/addImageDownload';
import pako from 'pako';
import ReactPlayer from 'react-player';

let isAiCustomer = localStorage.getItem('is_ai_customer') === 'true';

const AIEditorModalViewer = ({
    from,
    ai_page,
    isOpen,
    onClose,
    imageData,
    imageDisplayLoader,
    compareSiloModalOpen,
    setCompareSiloModalOpen,
    goToNextImage,
    goToPreviousImage,
    renderDelete,
    updatedRenderNameInImagesList,
    markFavorite,
    unmarkFavorite,
    promptsLoading,
    handleSubmitForReview,
    handleCollaborate,
    handleCollaborateAiImage,
    handleSubmitForReviewImageLibrary }) => {
    const [showHighResModal, setShowHighResModal] = useState(false);
    const [showEditPromptModal, setShowEditPromptModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const imageZoomRef = useRef(null);
    const [savePromptLoader, setSavePromptLoader] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);

    const isVideo = imageData?.asset_type === 'ai_video' || imageData?.image_type === 'ai_video';
    const [extractedResolution, setExtractedResolution] = useState(null);
    const [videoResolution, setVideoResolution] = useState(null);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        
        let date;
        const strValue = String(dateString);

        if (/^\d+(\.\d+)?$/.test(strValue)) {
            // Parse as Unix timestamp in milliseconds
            date = new Date(parseFloat(strValue));
        } else {
            const utcDateString = strValue.endsWith('Z') ? strValue : strValue + 'Z';
            date = new Date(utcDateString);
        }
        
        return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric"
        });
    };

    useEffect(() => {
        if (imageZoomRef.current && imageData?.selectedImageUrl) {
            setTimeout(() => {
                if (imageZoomRef.current) {
                    imageZoomRef.current.resetTransform();
                }
            }, 100);
        }
        // Reset extracted resolution and video resolution when imageData changes
        setExtractedResolution(null);
        setVideoResolution(null);
    }, [imageData]);

    // Add download button inside Ant Design Image preview (fullscreen view)
    // Only attach when modal is open to avoid conflicts with other observers
    useEffect(() => {
        if (!isOpen) return;
        
        const downloadImage = () => {
            if (imageData?.selectedImageUrl) {
                handleDownload();
            }
        };
        const observer = addDownloadIconObserver(downloadImage);
        return () => observer.disconnect();
    }, [isOpen, imageData]);


    const handleLike = () => {
        if (imageData?.isFavourite) {
            unmarkFavorite(imageData);
            imageData.isFavourite = false;
        } else {
            markFavorite(imageData);
            imageData.isFavourite = true;
        }
    };

    const handleDownload = () => {
        if (imageData?.selectedImageUrl) {
            let extension = imageData.selectedImageUrl.split('.').pop();
            downloadFile(imageData.selectedImageUrl, getImageName(), extension);
        }
    };

    const toBase64Url = (uint8) => {
        let binary = '';
        for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
        return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    };

    const compressState = (obj) => {
        const json = JSON.stringify(obj || {});
        const deflated = pako.deflate(json);
        return toBase64Url(deflated);
    };

    const goToProductDetails = () => {
        window.location.href = ('/products/' + imageData.product_id);
    };

    const goToGenerateMore = () => {
        console.log("imageData", imageData);
        const params = new URLSearchParams();
        params.set('stage', 'lifestyle_generation');
        params.set('productId', imageData.product_id);
        if (imageData.job_id) params.set('job_id', imageData.job_id);
        params.set('aiModel', 'true');

        const state = {
            img: imageData.input_image_uri ? imageData.input_image_uri : (imageData.src || ''),
            imgInspection: imageData.inspiration_image_uri || '',
            inspiration_id: imageData.inspiration_id || null,
            // Prefer job-level prompt (consistent with Activity page); fallback to per-render prompt if present.
            prompt: imageData.prompt_text || imageData.image_prompt || '',
            props: imageData.props || [],
            aspect_ratio: imageData.latest_aspect_ratio || "1:1",
        };
        params.set('state', compressState(state));

        window.location.href = `/ai-asset-creator?${params.toString()}`;
    };

    const goToAIEditor = () => {
        let prompt_text = imageData?.prompt_text;
        if (prompt_text == "") {
            prompt_text = imageData?.display_name;
        }
        let image_id = imageData.render_id || imageData.id;

        window.location.href = ('/edit-ai-image?imageUrl=' + imageData.selectedImageUrl + '&imageId=' + image_id + '&productId=' + imageData.product_id + '&promptText=' + prompt_text);
    };

    const compareSilo = () => {
        setCompareSiloModalOpen(true);
    };

    const handleEditPromptClick = () => {
        setShowEditPromptModal(true);
    };

    const handleSavePrompt = (editedText) => {
        setSavePromptLoader(true);
        console.log('Saving prompt:', editedText);
        let payload = {
            id: `${imageData.render_id || imageData.id}`,
            display_name: editedText,
            action: 'edit_ai_image_name'
        }
        if (isVideo) {
            payload['action'] = 'edit_ai_video_name';
        }
        console.log(payload)
        axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload)
            .then(res => {
                setSavePromptLoader(false);
                updatedRenderNameInImagesList(imageData, editedText)
                setShowEditPromptModal(false);
                message.success('Name changed successfully!');
            })
    };

    const handleCloseEditPrompt = () => {
        setShowEditPromptModal(false);
    };

    const closeCompareSiloModal = () => {
        setCompareSiloModalOpen(false);
    };

    const closeAllModals = () => {
        setCompareSiloModalOpen(false);
        setShowHighResModal(false);
        setShowEditPromptModal(false);
        onClose();
    };

    const getDisplayPrompt = () => {
        let prompt = imageData?.image_prompt || '';

        if (isVideo) {
            prompt = imageData?.video_prompt || '';
        }
        const cleanedPrompt = prompt.replace(/Keep the rest of the scene the same\.\s*/gi, '').trim();

        if (cleanedPrompt.toLowerCase().trim() === 'untitled') {
            return '';
        }
        return cleanedPrompt;
    };

    // Helper: join base URL and path ensuring exactly one slash between them
    // Preserves double slashes in path if they exist (e.g., //tmp/...)
    const joinUrl = (base, path) => {
        if (!base) return path || '';
        if (!path) return base;
        const b = base.endsWith('/') ? base.slice(0, -1) : base;
        const pathStr = String(path);
        // Check if path starts with double //tmp and preserve it
        if (pathStr.startsWith('//tmp')) {
            return `${b}${pathStr}`;
        }
        else if (pathStr.startsWith('/tmp')) {
            return `${b}/${pathStr}`;
        }
        // Otherwise, remove leading slashes and add one
        const p = pathStr.replace(/^\/+/, '');
        return `${b}/${p}`;
    };

    const getCorrectImageUrl = (imageUri) => {
        if (!imageUri) return '';

        if (imageUri.startsWith('http://') || imageUri.startsWith('https://')) {
            try {
                const url = new URL(imageUri);
                return joinUrl(ENVIRONMENT.getBaseURL(imageData?.platform), url.pathname);
            } catch (e) {
                try {
                    const fallbackPath = imageUri.replace(/^https?:\/\/[^/]+/, '');
                    return joinUrl(ENVIRONMENT.getBaseURL(imageData?.platform), fallbackPath);
                } catch (e2) {
                    return imageUri;
                }
            }
        }

        return joinUrl(ENVIRONMENT.getBaseURL(imageData?.platform), imageUri);
    };

    const getImageName = () => {
        if (imageData?.display_name) {
            return imageData?.display_name
        }

        if (imageData?.is_edit_request) {
            return "Edit " + imageData?.edit_number
        }
        else {
            return "Untitled"
        }
    }

    const isFromAiCreatorActivity = from === 'activity';
    const isUpscaleRequest = imageData?.request_type === 'upscale_request';
    const shouldShowPurchasedHighResCTA = isUpscaleRequest;

    return (
        <>
            {isOpen && !compareSiloModalOpen && imageData?.renders?.length > 1 &&
                <div className='ai-btn-carousel left' onClick={goToPreviousImage}>
                    <Button className='ai-transparent-black-btn pd-small' icon={<ArrowLeftOutlined />} />
                </div>}
            {isOpen && <div className='ai-image-viewer-modal-close-btn'>
                <Tooltip title={<span className="manrope f-12 white">Close</span>}>
                    <CloseOutlined className='manrope f-24 w-500 white' onClick={onClose} />
                </Tooltip>
            </div>}
            <div className='ai-image-viewer-modal-container'>
                <Modal
                    className='ai-image-viewer-modal'
                    visible={isOpen}
                    destroyOnClose={true}
                    onCancel={onClose}
                    closable={false}
                    footer={[
                    ]}
                    centered={true}>
                    <Row>
                        <Col span={24} className='j-start-a-start '>
                            <Col span={5} className='prompt-box'>
                                <div className="manrope f-20 lh-28 w-500 black-085 justify-in-start">
                                    <div className='ai-gradient-container'>
                                        <img src="/img/ai_creator/ai-twinkle-gradient.png" />
                                        <span className='manrope f-14 w-600 lh-28 ai-gradient ml-4'>AI</span>
                                    </div>

                                    {imageData && imageData.request_type == 'edit_request' && imageData.renders && (
                                        <div className="header-engine-logo-badge">
                                            <span className="engine-name">Edited</span>
                                        </div>
                                    )}
                                </div>
                                <div className="mb-8 mt-16 manrope f-20 lh-28 w-500 black-085 justify-in-start">
                                    <span className="manrope f-20 lh-28 w-500 black-085 ai-prompt-text">
                                        <Tooltip placement={'bottomLeft'} title={<span className="manrope f-12 white">{getImageName()}</span>}>
                                            {getImageName()}
                                        </Tooltip>
                                    </span>
                                    <Tooltip title={<span className="manrope f-12 white">Edit Name</span>}>
                                        <EditOutlined
                                            className='ml-8 manrope blue cursor-pointer'
                                            onClick={handleEditPromptClick}
                                        />
                                    </Tooltip>
                                </div>
                                <div className='mb-24'>
                                    <span className="manrope f-12 w-500 lh-20 gray-045">
                                        Created on {formatDate(imageData?.render_submitted_on)}&nbsp;
                                    </span>
                                    <span className="manrope f-12 w-500 lh-20 gray-045">
                                        by <b>{imageData?.customer_username}</b>
                                    </span>
                                </div>


                                <div className='prompt-img-container-wrapper'>
                                    <div className='prompt-img-container'>
                                        {!(isVideo) && (
                                            <div className='prompt-side-img-container'>
                                                <Tooltip title={<span className="manrope f-12 white">Product Image</span>}>
                                                    <Card className='prompt-side-img-card'>
                                                        <Image
                                                            className='prompt-side-img'
                                                            src={getCorrectImageUrl(imageData?.input_image_uri || imageData?.src)}
                                                            preview={{
                                                                mask: <div className='manrope f-12 white'>Preview</div>
                                                            }}
                                                        />
                                                    </Card>
                                                </Tooltip>

                                                {imageData?.inspiration_image_uri && (
                                                    <Tooltip title={<span className="manrope f-12 white">Inspiration Image</span>}>
                                                        <Card className='prompt-side-img-card'>
                                                            <Image
                                                                className='prompt-side-img'
                                                                src={getCorrectImageUrl(imageData?.inspiration_image_uri)}
                                                                preview={{
                                                                    mask: <div className='manrope f-12 white'>Preview</div>
                                                                }}
                                                            />
                                                        </Card>
                                                    </Tooltip>
                                                )}
                                            </div>)}

                                        {/* {(imageData?.is_edit_request || imageData?.request_type == 'edit_request') && imageData?.original_input_image_uri && (
                                            <div className='prompt-side-img-container props-images-container'>
                                                <Tooltip 
                                                    title={<span className="manrope f-12 white">Input Image</span>}
                                                >
                                                    <Card className='prompt-side-img-card'>
                                                        <Image 
                                                            className='prompt-side-img' 
                                                            src={getCorrectImageUrl(imageData?.original_input_image_uri)}
                                                            preview={{
                                                                mask: <div className='manrope f-12 white'>Preview</div>
                                                            }}
                                                        />
                                                    </Card>
                                                </Tooltip>
                                            </div>
                                        )} */}
                                        {isVideo && (
                                            <div className='prompt-side-img-container'>
                                                <Tooltip title={<span className="manrope f-12 white">Final Image</span>}>
                                                    <Card className='prompt-side-img-card'>
                                                        <Image
                                                            className='prompt-side-img'
                                                            src={getCorrectImageUrl(imageData?.video_final_image_uri)}
                                                            preview={{
                                                                mask: <div className='manrope f-12 white'>Preview</div>
                                                            }}
                                                        />
                                                    </Card>
                                                </Tooltip>
                                            </div>
                                        )}

                                        {/* Props Images */}
                                        {imageData?.props_image_uris && imageData.props_image_uris.length > 0 && (
                                            <div className='prompt-side-img-container props-images-container'>
                                                {imageData.props_image_uris.map((propUri, idx) => (
                                                    <Tooltip
                                                        key={`prop-${idx}`}
                                                        title={<span className="manrope f-12 white">Prop {idx + 1}</span>}
                                                    >
                                                        <Card className='prompt-side-img-card'>
                                                            <Image
                                                                className='prompt-side-img'
                                                                src={getCorrectImageUrl(propUri)}
                                                                preview={{
                                                                    mask: <div className='manrope f-12 white'>Preview</div>
                                                                }}
                                                            />
                                                        </Card>
                                                    </Tooltip>
                                                ))}
                                            </div>
                                        )}

                                        <h3 className='manrope f-14 w-500 lh-22 black-085 mt-16'>
                                            {promptsLoading && !getDisplayPrompt() ? (
                                                <span className='gray-045'>
                                                    <LoadingOutlined className='mr-8' />
                                                    Loading prompt...
                                                </span>
                                            ) : (
                                                getDisplayPrompt()
                                            )}
                                        </h3>
                                    </div>
                                </div>
                            </Col>
                            <Col span={19} className='ai-img-box'>
                                <div className='ai-image-container'>
                                    <div className='ai-image-btns-container'>
                                        <div className='justify-in-start'>
                                            {!(isVideo) && (
                                                <Tooltip title={<span className="manrope f-12 white">Collaborate</span>}>
                                                    <Button className='manrope f-14 w-500 lh-22 ai-img-btns'
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            isFromAiCreatorActivity ? handleCollaborate(imageData?.renderIndex, imageData) : handleCollaborateAiImage(imageData);
                                                        }}>
                                                        <img src={'/img/collaborate_tool/new-new-collab-1.2.svg'} width={16} height={18} />
                                                        <span className='ml-8 manrope f-14 w-500'>Collaborate</span>
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            {!(isVideo) && (
                                                <Tooltip title={<span className="manrope f-12 white">Edit Image</span>}>
                                                    <Button className='manrope f-14 w-500 lh-22 ai-img-btns ml-8' onClick={goToAIEditor}>
                                                        <EditOutlined />Edit
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            {!(isVideo) && (
                                                <Tooltip title={<span className="manrope f-12 white">Compare with silo</span>}>
                                                    <Button className='manrope f-14 w-500 lh-22 ai-img-btns ml-8' onClick={compareSilo}>
                                                        <ForkOutlined />Compare
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </div>
                                        <div className='justify-in-end'>
                                            <Tooltip title={<span className="manrope f-12 white">{imageData?.isFavourite ? "Unfavorite" : "Favorite"}</span>}>
                                                <Button className='ai-heart-btn' onClick={handleLike}>
                                                    {imageData?.isFavourite ? <HeartFilled /> : <HeartOutlined />}
                                                </Button>
                                            </Tooltip>
                                            {!(isVideo) && (
                                                <Tooltip title={<span className="manrope f-12 white">Expand Image</span>}>
                                                    <Button className='ai-fullscreen-btn ml-8' onClick={() => setPreviewVisible(true)}>
                                                        <FullscreenOutlined />
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>

                                    {isVideo ? (
                                        <div className="react-player-wrapper" style={{ width: '100%', height: '100%', background: 'black', borderRadius: '0px', overflow: 'hidden' }}>
                                            <ReactPlayer
                                                url={imageData?.selectedImageUrl}
                                                width="100%"
                                                height="100%"
                                                playing={true}
                                                controls={true}
                                                onReady={(player) => {
                                                    setTimeout(() => {
                                                        const internalPlayer = player.getInternalPlayer();
                                                        if (internalPlayer && internalPlayer.videoWidth && internalPlayer.videoHeight) {
                                                            setVideoResolution({
                                                                width: internalPlayer.videoWidth,
                                                                height: internalPlayer.videoHeight
                                                            });
                                                        }
                                                    }, 500);
                                                }}
                                                style={{ objectFit: 'contain' }}
                                                config={{
                                                    file: {
                                                        attributes: {
                                                            style: {
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'contain',
                                                                backgroundColor: 'black'
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <ImageZoom
                                            ref={imageZoomRef}
                                            imageSrc={imageData?.selectedImageUrl}
                                            className="ai-image"
                                            onImageLoad={(resolution) => {
                                                setExtractedResolution(resolution);
                                            }}
                                        />
                                    )}


                                    {(imageData?.width || imageData?.width_dim) && !(isVideo) && (
                                        <div className='image-resolution-display bottom-58'>
                                            <span className='manrope f-14 lh-20 w-500 white'>
                                                {ai_page ? imageData?.width_dim : imageData?.width}”W x {ai_page ? imageData?.height_dim : imageData?.height}”H x {ai_page ? imageData?.depth_dim : imageData?.depth}”D
                                            </span>
                                        </div>
                                    )}

                                    {(videoResolution || extractedResolution || (imageData?.image_width && imageData?.image_height)) && (
                                        <div className='image-resolution-display' style={{ bottom: isVideo ? '74px' : '24px', left: isVideo ? '10px' : undefined }}>
                                            <span className='manrope f-14 lh-20 w-500 white'>
                                                {isVideo
                                                    ? (videoResolution
                                                        ? `${videoResolution.width} x ${videoResolution.height}`
                                                        : '')
                                                    : extractedResolution
                                                        ? `${extractedResolution.width} x ${extractedResolution.height}`
                                                        : `${imageData?.image_width} x ${imageData?.image_height}`
                                                }
                                            </span>
                                        </div>
                                    )}

                                    {imageDisplayLoader && !(isVideo) ?
                                        <LoadingOutlined className='carousel-img-loader' />
                                        : ""}

                                </div>

                                <div className='ai-image-buttons'>
                                    <div className='justify-in-start' style={{ display: 'flex', gap: '8px' }}>
                                    {!isVideo && (
                                            <>
                                                <Tooltip title={<span className="manrope f-12 white">Submit for review</span>}>
                                                    <Button 
                                                        className='manrope f-14 w-500 lh-22 ai-action-btn'
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            isFromAiCreatorActivity ? handleSubmitForReview(imageData?.renderIndex, imageData) : handleSubmitForReviewImageLibrary(imageData);
                                                        }}
                                                    > 
                                                        <img src={'/img/ai_creator/submit_for_review-black.svg'} width={16} height={18} />   
                                                    </Button>
                                                </Tooltip>
                                            </>
                                        )}
                                        <Tooltip title={<span className="manrope f-12 white">{isVideo ? "Download Video" : (isUpscaleRequest ? "Download High-Res Image" : "Download Preview")}</span>}>
                                            <Button
                                                className='manrope f-14 w-500 lh-22 ai-action-btn'
                                                icon={<DownloadOutlined />}
                                                onClick={handleDownload}
                                            />
                                        </Tooltip>
                                        <Tooltip title={<span className="manrope f-12 white">Share</span>}>
                                            <Button className='manrope f-14 w-500 lh-22 ai-action-btn' icon={<LinkOutlined />} onClick={() => setShowShareModal(true)} />
                                        </Tooltip>

                                        <Tooltip title={<span className="manrope f-12 white">Product Details</span>}>
                                            <Button onClick={goToProductDetails} className='manrope f-14 w-500 lh-22 ai-action-btn' icon={<UnorderedListOutlined />} />
                                        </Tooltip>

                                        {!isVideo && (
                                            <Tooltip title={<span className="manrope f-12 white">Generate More</span>}>
                                                <Button className='manrope f-14 w-500 lh-22 ai-action-btn' icon={<RedoOutlined />} onClick={goToGenerateMore} />
                                            </Tooltip>
                                        )}

                                        {renderDelete ? renderDelete(imageData) :
                                            <Tooltip title={<span className="manrope f-12 white">Delete</span>}>
                                                <Button className='manrope f-14 w-500 lh-22 ai-action-btn ai-action-btn-red' icon={<DeleteOutlined />} />
                                            </Tooltip>}
                                    </div>

                                    <div className='justify-in-end' style={{ display: 'flex', gap: '8px' }}>
                                        {(shouldShowPurchasedHighResCTA || isVideo) ? (
                                            <>
                                                <div className='manrope f-14 w-700 lh-22 green-73'>
                                                    <CheckCircleOutlined className='manrope f-14 green-73' />
                                                    <span className='ml-6'>Purchased</span>
                                                    <span className='manrope f-14 w-500 lh-22 black-085 ml-8'>No additional credits needed</span>
                                                </div>
                                                <Button
                                                    className='manrope f-14 w-500 lh-22 ai-action-btn-blue'
                                                    onClick={handleDownload}
                                                >
                                                    {isVideo ? 'Download Video' : 'Download High-Res Image'}
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                {
                                                    imageData?.final_image_generated ?
                                                    <div className='manrope f-14 w-700 lh-22 green-73'>
                                                        <CheckCircleOutlined className='manrope f-14 green-73' />
                                                        <span className='ml-6'>Purchased</span>
                                                        <span className='manrope f-14 w-500 lh-22 black-085 ml-8'>No additional credits needed</span>
                                                    </div>
                                                    :
                                                    ""
                                                }
                                                <Button
                                                    className='manrope f-14 w-500 lh-22 ai-action-btn-blue'
                                                    onClick={() => setShowHighResModal(true)}
                                                >
                                                    Save As Final: No Watermark
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </Col>
                        </Col>
                    </Row>

                    <HighResModal
                        isOpen={showHighResModal}
                        onClose={() => setShowHighResModal(false)}
                        imageData={imageData}
                    />
                    <ShareModal
                        isOpen={showShareModal}
                        onClose={() => setShowShareModal(false)}
                        imageData={imageData}
                    />
                    <CompareSiloView
                        ai_page={ai_page}
                        isOpen={compareSiloModalOpen}
                        onClose={closeCompareSiloModal}
                        closeAllModals={closeAllModals}
                        imageData={imageData}
                    />
                    <TextEditor
                        isOpen={showEditPromptModal}
                        onClose={handleCloseEditPrompt}
                        initialText={getImageName()}
                        onSave={handleSavePrompt}
                        placeholder={isVideo ? "Enter video name..." : "Enter image name..."}
                        rows={4}
                        loader={savePromptLoader}
                        isVideo={isVideo}
                    />
                </Modal>
            </div>

            {isOpen && !compareSiloModalOpen && imageData?.renders?.length > 1 &&
                <div className='ai-btn-carousel right' onClick={goToNextImage}>
                    <Button className='ai-transparent-black-btn pd-small' icon={<ArrowRightOutlined />} />
                </div>}

            {isOpen && (
                <Image
                    style={{ display: 'none' }}
                    src={imageData?.selectedImageUrl}
                    preview={{
                        visible: previewVisible,
                        onVisibleChange: (visible) => setPreviewVisible(visible),
                    }}
                />
            )}
        </>
    );
};

export default AIEditorModalViewer;
